import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createServerSupabaseClient, getSessionFromSSR, getCurrentUserFromSSR, hasBusinessOrAdminPermission } from '@/services/supabase';
import { 
  MagnifyingGlassIcon, 
  ArrowPathIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  TruckIcon,
  FunnelIcon,
  ShoppingBagIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

type Order = {
  id: string;
  customer: {
    id: string;
    name: string;
    email: string;
  };
  date: string;
  total: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  items: {
    id: string;
    name: string;
    quantity: number;
    price: number;
  }[];
};

// Server-side function that can be used with Next.js getServerSideProps
export async function getOrdersDataSSR(context: any) {
  try {
    // Create a server-side Supabase client
    const supabaseClient = createServerSupabaseClient(context);
    
    // Get the session using the SSR method
    const session = await getSessionFromSSR(supabaseClient);
    
    // If no session, return null
    if (!session) {
      return { user: null, data: null };
    }
    
    // Get the user using the SSR method
    const user = await getCurrentUserFromSSR(supabaseClient);
    
    // Check if user has proper permissions using the centralized function
    if (!hasBusinessOrAdminPermission(user)) {
      return { user: null, data: null };
    }
    
    // In a real implementation, fetch orders data from API
    // For now, return mock data
    const ordersData = [
      {
        id: '1001',
        customer: {
          id: 'c101',
          name: 'Local Food Bank',
          email: 'contact@localfoodbank.org'
        },
        date: '2025-05-05T14:30:00Z',
        total: 125.50,
        status: 'completed',
        items: [
          { id: 'i101', name: 'Organic Apples (5lb bag)', quantity: 5, price: 4.99 },
          { id: 'i102', name: 'Whole Grain Bread', quantity: 10, price: 2.99 },
          { id: 'i103', name: 'Fresh Milk (1 gallon)', quantity: 8, price: 3.49 }
        ]
      },
      // Add more mock orders as needed
    ];
    
    return {
      user,
      data: ordersData
    };
  } catch (error) {
    console.error('Error in getOrdersDataSSR:', error);
    return { user: null, data: null };
  }
}

// Props interface for Orders component to accept SSR data
interface OrdersProps {
  initialUser?: any;
  initialData?: any;
}

export default function Orders({ initialUser, initialData }: OrdersProps = {}) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [notificationMessage, setNotificationMessage] = useState('');

  useEffect(() => {
    // If we have initialData from SSR, use it directly
    if (initialData && initialUser) {
      setOrders(initialData);
      setIsLoading(false);
    } else {
      // Otherwise use the existing client-side method
      fetchOrders();
    }
  }, [initialData, initialUser]);

  const cancelOrder = async (orderId: string) => {
    try {
      // In a real app, this would be an API call
      // await api.put(`/orders/${orderId}`, { status: 'cancelled' });
      
      // For the demo, we'll update the local state directly
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status: 'cancelled' as const }
            : order
        )
      );
      
      toast.success(`Order #${orderId} has been cancelled`);
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order');
    }
  };

  const sendNotification = async () => {
    try {
      if (!selectedOrder || !notificationMessage) {
        toast.error('Please provide a notification message');
        return;
      }
      
      // In a real app, this would be an API call
      // await api.post(`/orders/${selectedOrder.id}/notifications`, { message: notificationMessage });
      
      toast.success(`Notification sent to ${selectedOrder?.customer?.name || 'customer'}`);
      setShowNotificationModal(false);
      setNotificationMessage('');
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    }
  };

  const openNotificationModal = (order: Order) => {
    setSelectedOrder(order);
    setShowNotificationModal(true);
  };

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      // In a real app, this would be an API call
      // const data = await getOrders();
      const data = [
        {
          id: '1001',
          customer: {
            id: 'c101',
            name: 'Local Food Bank',
            email: 'contact@localfoodbank.org'
          },
          date: '2025-05-05T14:30:00Z',
          total: 125.50,
          status: 'completed' as const,
          items: [
            { id: 'i101', name: 'Organic Apples (5lb bag)', quantity: 5, price: 4.99 },
            { id: 'i102', name: 'Whole Grain Bread', quantity: 10, price: 2.99 },
            { id: 'i103', name: 'Fresh Milk (1 gallon)', quantity: 8, price: 3.49 }
          ]
        },
        {
          id: '1002',
          customer: {
            id: 'c102',
            name: 'Community Kitchen',
            email: 'info@communitykitchen.org'
          },
          date: '2025-05-05T10:15:00Z',
          total: 78.25,
          status: 'processing' as const,
          items: [
            { id: 'i104', name: 'Mixed Berries', quantity: 4, price: 3.99 },
            { id: 'i105', name: 'Organic Spinach', quantity: 6, price: 1.99 },
            { id: 'i106', name: 'Chicken Breast (1lb)', quantity: 5, price: 5.99 }
          ]
        },
        {
          id: '1003',
          customer: {
            id: 'c103',
            name: 'Shelter Outreach',
            email: 'coordinator@shelteroutreach.org'
          },
          date: '2025-05-04T16:45:00Z',
          total: 210.00,
          status: 'pending' as const,
          items: [
            { id: 'i107', name: 'Brown Rice (5lb bag)', quantity: 10, price: 6.99 },
            { id: 'i108', name: 'Canned Beans', quantity: 24, price: 1.29 },
            { id: 'i109', name: 'Pasta (1lb)', quantity: 15, price: 1.99 },
            { id: 'i110', name: 'Pasta Sauce', quantity: 15, price: 2.49 }
          ]
        },
        {
          id: '1004',
          customer: {
            id: 'c104',
            name: 'Youth Center',
            email: 'programs@youthcenter.org'
          },
          date: '2025-05-03T11:30:00Z',
          total: 45.75,
          status: 'completed' as const,
          items: [
            { id: 'i111', name: 'Granola Bars (Box of 12)', quantity: 5, price: 4.99 },
            { id: 'i112', name: 'Apple Juice (64oz)', quantity: 8, price: 2.99 }
          ]
        },
        {
          id: '1005',
          customer: {
            id: 'c105',
            name: 'Senior Center',
            email: 'care@seniorcenter.org'
          },
          date: '2025-05-02T09:15:00Z',
          total: 92.30,
          status: 'cancelled' as const,
          items: [
            { id: 'i113', name: 'Frozen Vegetables', quantity: 12, price: 2.49 },
            { id: 'i114', name: 'Soup Cans', quantity: 24, price: 1.99 },
            { id: 'i115', name: 'Yogurt (32oz)', quantity: 6, price: 3.99 }
          ]
        },
      ];
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'processing':
        return <TruckIcon className="h-4 w-4 text-blue-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusText = (status: Order['status']) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusClass = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Format: May 14, 2025, 9:30 AM
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }) + ', ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredOrders = orders.filter(order => {
    // Apply search filter
    if (searchTerm && !order.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) && !order.id.includes(searchTerm)) {
      return false;
    }
    
    // Apply status filter
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }
    
    // Apply date filter
    if (dateFilter !== 'all') {
      const orderDate = new Date(order.date);
      const today = new Date();
      
      if (dateFilter === 'today') {
        return orderDate.toDateString() === today.toDateString();
      }
      
      if (dateFilter === 'this-week') {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Start of current week (Sunday)
        startOfWeek.setHours(0, 0, 0, 0);
        return orderDate >= startOfWeek;
      }
      
      if (dateFilter === 'this-month') {
        return orderDate.getMonth() === today.getMonth() && orderDate.getFullYear() === today.getFullYear();
      }
    }
    
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Page Header */}
          <div className="flex items-center mb-6">
            <button
              type="button"
              onClick={() => navigate('/business/dashboard')}
              className="mr-4 text-gray-500 hover:text-navy-blue"
            >
            </button>
            <h1 className="text-xl font-medium text-navy-blue">Orders</h1>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search orders..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white shadow-sm rounded-lg p-4 border-b-4 border-blue-500">
            <div className="flex justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Orders</p>
                <p className="text-2xl font-bold">{orders.length}</p>
              </div>
              <div className="rounded-full bg-blue-100 p-2">
                <ShoppingBagIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white shadow-sm rounded-lg p-4 border-b-4 border-green-500">
            <div className="flex justify-between">
              <div>
                <p className="text-gray-500 text-sm">Completed</p>
                <p className="text-2xl font-bold">{orders.filter(o => o.status === 'completed').length}</p>
              </div>
              <div className="rounded-full bg-green-100 p-2">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white shadow-sm rounded-lg p-4 border-b-4 border-yellow-500">
            <div className="flex justify-between">
              <div>
                <p className="text-gray-500 text-sm">Pending</p>
                <p className="text-2xl font-bold">{orders.filter(o => o.status === 'pending').length}</p>
              </div>
              <div className="rounded-full bg-yellow-100 p-2">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white shadow-sm rounded-lg p-4 border-b-4 border-red-500">
            <div className="flex justify-between">
              <div>
                <p className="text-gray-500 text-sm">Cancelled</p>
                <p className="text-2xl font-bold">{orders.filter(o => o.status === 'cancelled').length}</p>
              </div>
              <div className="rounded-full bg-red-100 p-2">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
          <div className="flex items-center mb-3">
            <FunnelIcon className="h-5 w-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Filters</h2>
          </div>
          <div className="flex flex-wrap gap-4">
            <select
              className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm rounded-md"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="this-week">This Week</option>
              <option value="this-month">This Month</option>
            </select>
            <button
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sky-blue hover:bg-teal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
              onClick={fetchOrders}
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Refresh
            </button>
          </div>
        </div>
        return (
        {/* Orders Table */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue"></div>
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="bg-white shadow-md rounded-lg overflow-hidden w-full">
            <div className="overflow-x-auto">
              <table className="w-full min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-navy-blue">
                        #{order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.customer.name}</div>
                        <div className="text-sm text-gray-500">{order.customer.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(order.total)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(order.status)}
                          <span className={`ml-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.items.length} items
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/orders/${order.id}`}
                            className="text-sky-blue hover:text-teal"
                            title="View Order"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </Link>
                          
                          <button
                            onClick={() => openNotificationModal(order)}
                            className="text-navy-blue hover:text-sky-blue"
                            title="Send Notification"
                          >
                            <BellIcon className="h-5 w-5" />
                          </button>
                          
                          {(order.status === 'pending' || order.status === 'processing') && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to cancel order #${order.id}?`)) {
                                  cancelOrder(order.id);
                                }
                              }}
                              className="text-red-600 hover:text-red-800"
                              title="Cancel Order"
                            >
                              <XCircleIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <p className="text-gray-500">No orders found.</p>
            {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
              <button
                className="mt-2 text-sky-blue hover:text-teal"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setDateFilter('all');
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    {/* Notification Modal */}
    {showNotificationModal && (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="border-b px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <BellIcon className="h-5 w-5 mr-2" />
              Send Notification
            </h3>
            <button 
              onClick={() => {
                setShowNotificationModal(false);
                setNotificationMessage('');
                setSelectedOrder(null);
              }}
              className="text-gray-400 hover:text-gray-500"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-4">
            {selectedOrder && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-1">Sending notification about:</p>
                <p className="font-medium">Order #{selectedOrder?.id} - {selectedOrder?.customer?.name}</p>
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="notification" className="block text-sm font-medium text-gray-700 mb-1">
                Notification Message
              </label>
              <textarea
                id="notification"
                rows={4}
                className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-navy-blue focus:border-navy-blue"
                placeholder="Enter your notification message here..."
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
              />
            </div>
            
            <div className="flex justify-end space-x-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setShowNotificationModal(false);
                  setNotificationMessage('');
                  setSelectedOrder(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendNotification}
                disabled={!notificationMessage.trim()}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white bg-navy-blue hover:bg-sky-blue focus:outline-none ${!notificationMessage.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Send Notification
              </button>
            </div>
          </div>
        </div>
        </div>
         )}
      </div> 
    )};
